import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useNavigate } from 'react-router-dom'
import { AnimatePresence, MotionConfig, motion, useReducedMotion, type PanInfo } from 'motion/react'
import { CARD_RADIUS, DOCK_SURFACE_ID, MORPH_SPRING } from './dockMorph'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import { Avatar } from '@/components/ui/Avatar'
import { usePermissions } from '@/hooks/usePermission'
import { displayName } from './account'
import { NAV_TABS } from './BottomTabBar'
import { navItems, visibleNavItems } from './navItems'
import { LogOut } from 'lucide-react'

/**
 * Everything the dock doesn't already show — never a second copy of a dock
 * tab (UX-112: Work and Customers used to appear in both). Derived from the
 * sidebar's list (with its capabilities) so the two navigations can't drift.
 */
const DOCK_PATHS = new Set<string>(NAV_TABS.map((t) => t.path))
export const MORE_ITEMS = navItems.filter((i) => !DOCK_PATHS.has(i.path))

/**
 * "More" card. The pill's surface (layoutId DOCK_SURFACE_ID in BottomTabBar)
 * morphs into this card: width, height and corner radius spring up and out,
 * the tab icons fade out, and the grid staggers in over a dimmed, blurred
 * backdrop. Closing reverses it back into the pill. Dismiss by tapping outside, pressing Escape, or dragging
 * it down. Under prefers-reduced-motion it appears and disappears with no morph or spring.
 */
const ITEM = {
  hidden: { opacity: 0, y: 10, scale: 0.92, transition: { duration: 0.12 } },
  shown: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 420, damping: 30 } },
} as const
// Reduced motion: no morph, no spring, no stagger. The card just appears.
const ITEM_REDUCED = {
  hidden: { opacity: 0, transition: { duration: 0 } },
  shown: { opacity: 1, transition: { duration: 0 } },
} as const
const INSTANT = { duration: 0 } as const

export function MoreMenuSheet() {
  const { moreMenuOpen, setMoreMenuOpen } = useUIStore()
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const close = () => setMoreMenuOpen(false)
  const reduce = useReducedMotion()
  const { can, roleLabel } = usePermissions()
  const items = visibleNavItems(MORE_ITEMS, can)
  const itemVariants = reduce ? ITEM_REDUCED : ITEM
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!moreMenuOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMoreMenuOpen(false) }
    window.addEventListener('keydown', onKey)
    const t = window.setTimeout(() => {
      cardRef.current?.querySelector<HTMLElement>('a')?.focus({ preventScroll: true })
    }, 80)
    return () => { window.removeEventListener('keydown', onKey); window.clearTimeout(t) }
  }, [moreMenuOpen, setMoreMenuOpen])

  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > 80 || info.velocity.y > 600) close()
  }

  return createPortal(
    <MotionConfig reducedMotion="user">
    <AnimatePresence>
      {moreMenuOpen && (
        <div className="md:hidden" key="more-layer">
          <motion.div
            aria-hidden="true"
            data-testid="more-backdrop"
            onClick={close}
            className="fixed inset-0 z-[70] bg-black/60"
            style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: reduce ? 0 : 0.22 } }}
            exit={{ opacity: 0, transition: { duration: reduce ? 0 : 0.18 } }}
          />
          <motion.div
            ref={cardRef}
            layoutId={reduce ? undefined : DOCK_SURFACE_ID}
            role="dialog"
            aria-modal="true"
            aria-label="More"
            className="fixed z-[71] left-3 right-3 p-3 pt-2 overflow-hidden"
            transition={reduce ? INSTANT : MORPH_SPRING}
            style={{
              // Sits just above the 68px dock, clear of the home indicator.
              bottom: 'calc(max(6px, calc(var(--safe-bottom, 0px) - 28px)) + 62px + 12px)',
              borderRadius: CARD_RADIUS,
              // Same material as the pill so the morph reads as one surface.
              background: 'rgba(14,14,17,0.94)',
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)',
              border: '0.5px solid rgba(255,255,255,0.10)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 30px 80px -20px rgba(0,0,0,0.9), 0 10px 30px rgba(0,0,0,0.5)',
            }}
            drag={reduce ? false : 'y'}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.05, bottom: 0.6 }}
            onDragEnd={onDragEnd}
          >
            <motion.div
              initial="hidden"
              animate="shown"
              exit="hidden"
              variants={{
                hidden: { transition: reduce ? INSTANT : { staggerChildren: 0.02, staggerDirection: -1 } },
                shown: { transition: reduce ? INSTANT : { delayChildren: 0.1, staggerChildren: 0.035 } },
              }}
            >
              {/* Grab handle — signals drag-to-dismiss */}
              <motion.div variants={itemVariants} className="flex justify-center pb-2" aria-hidden="true">
                <span className="block h-1 w-9 rounded-full bg-white/20" />
              </motion.div>
              <div className="grid grid-cols-3 gap-1">
                {items.map(({ label, path, icon: Icon }) => (
                  <motion.div key={path} variants={itemVariants}>
                    <NavLink
                      to={path}
                      onClick={close}
                      className={({ isActive }) => cn(
                        'flex flex-col items-center justify-center gap-1.5 p-3 rounded-2xl min-h-[76px]',
                        'transition-colors duration-150 active:opacity-70',
                        isActive
                          ? 'bg-white/[0.08] text-ink-50'
                          : 'text-ink-300 hover:bg-white/[0.04] hover:text-ink-100'
                      )}
                    >
                      <Icon className="w-6 h-6" />
                      <span className="text-[12px] font-medium leading-tight text-center">{label}</span>
                    </NavLink>
                  </motion.div>
                ))}
              </div>
              {/* Who is signed in, and the way out — the phone had no sign-out
                  outside the small avatar menu in the header. */}
              <motion.div
                variants={itemVariants}
                className="mt-2 flex items-center gap-3 rounded-2xl px-3 py-2"
                style={{ background: 'rgba(255,255,255,0.04)' }}
              >
                <Avatar name={displayName(user?.name)} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] font-semibold text-white">{displayName(user?.name)}</span>
                  <span className="block truncate text-[12px]" style={{ color: 'var(--midnight-text3)' }}>{roleLabel}</span>
                </span>
                <button
                  type="button"
                  onClick={async () => { close(); await logout(); navigate('/login') }}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-full px-3 text-[14px] font-medium text-ink-200 hover:bg-white/[0.06]"
                >
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
    </MotionConfig>,
    document.body,
  )
}
